import React, { useState, useEffect } from 'react';
import { X, Tag, Clock } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

interface CampaignData {
  active: boolean;
  title: string;
  subtitle: string;
  discount_text: string;
  background_color: string;
  background_image: string | null;
  text_color: string;
  button_text: string;
  button_link: string;
  valid_until: string | null;
}

const CampaignSection: React.FC = () => {
  const [campaign, setCampaign] = useState<CampaignData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchCampaign();
    
    // Check if user has dismissed the campaign in this session
    const dismissedCampaign = sessionStorage.getItem('campaign-dismissed');
    if (dismissedCampaign) {
      setDismissed(true);
    }
  }, []);

  const fetchCampaign = async () => {
    try {
      const { data, error } = await supabase
        .from('site_content')
        .select('content')
        .eq('key', 'campaign_data')
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (data?.content) {
        const campaignData = JSON.parse(data.content);
        
        // Check if campaign is active and not expired
        if (campaignData.active) {
          if (campaignData.valid_until) {
            const validUntil = new Date(campaignData.valid_until);
            const now = new Date();
            if (now > validUntil) {
              setCampaign(null);
              setLoading(false);
              return;
            }
          }
          setCampaign(campaignData);
        }
      }
    } catch (err) {
      console.error('Error fetching campaign:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('campaign-dismissed', 'true');
  };

  const formatValidUntil = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading || !campaign || dismissed) {
    return null;
  }

  return (
    <section className="py-16 md:py-20 border-t border-slate-800">
      <div className="container mx-auto px-4 max-w-6xl">
        <div
          className="relative overflow-hidden border border-slate-800 bg-slate-900/40"
          style={{ color: campaign.text_color || undefined }}
        >
          {campaign.background_image && (
            <div
              className="absolute inset-0 bg-cover bg-center opacity-40"
              style={{ backgroundImage: `url(${campaign.background_image})` }}
              aria-hidden
            />
          )}
          <div
            className="absolute inset-0 opacity-70"
            aria-hidden
            style={{
              background:
                campaign.background_color
                  ? `radial-gradient(1200px circle at 20% 20%, ${campaign.background_color}40, transparent 55%), radial-gradient(900px circle at 85% 10%, rgba(56,189,248,0.20), transparent 55%)`
                  : undefined
            }}
          />

          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 z-10 p-2 hover:bg-slate-800/60 rounded-none transition-colors border border-slate-700 bg-slate-800/40"
            title="Stäng kampanj"
          >
            <X className="text-slate-200" size={20} />
          </button>

          <div className="relative z-10 p-6 md:p-8 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 mb-4">
                <Tag className="w-5 h-5 text-purple-400" />
                <span className="text-sm font-medium text-slate-300">Kampanj</span>
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                {campaign.title}
              </h2>

              {campaign.subtitle && (
                <p className="text-lg md:text-xl text-slate-300 leading-relaxed whitespace-pre-line">
                  {campaign.subtitle}
                </p>
              )}
            </div>

            <div className="lg:col-span-6">
              <div className="flex flex-col sm:flex-row items-stretch gap-4 sm:items-center sm:justify-end">
                {campaign.discount_text && (
                  <div className="bg-slate-800/60 border border-slate-700 px-6 py-3">
                    <span className="text-xl md:text-2xl font-bold text-white">
                      {campaign.discount_text}
                    </span>
                  </div>
                )}

                {campaign.button_text && campaign.button_link && (
                  <a
                    href={campaign.button_link}
                    className="inline-flex items-center justify-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-none transition-colors duration-300"
                  >
                    {campaign.button_text}
                    <Tag className="ml-2 text-purple-200" size={18} />
                  </a>
                )}
              </div>

              {campaign.valid_until && (
                <div className="mt-4 flex items-center text-sm text-slate-400 opacity-95">
                  <Clock className="w-4 h-4 mr-2 text-purple-300" />
                  <span>Gäller till {formatValidUntil(campaign.valid_until)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CampaignSection;